
import { useEffect, useState, useRef } from "react";
import { RankCard } from "./RankCard";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getStreakRank, getRankDescription } from "@/lib/streakRanks";

interface AnimatedRankCardProps {
  streak: number;
  isVisible: boolean;
  onClose: () => void;
  isLivestream?: boolean;
}

export const AnimatedRankCard = ({ streak, isVisible, onClose, isLivestream = false }: AnimatedRankCardProps) => {
  const [animationComplete, setAnimationComplete] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isRendered, setIsRendered] = useState(false);
  
  useEffect(() => {
    if (isVisible) {
      // First render the component
      setIsRendered(true);
      setAnimationComplete(false);
      
      // Then complete the animation after a delay
      const timer = setTimeout(() => {
        setAnimationComplete(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    } else if (!isVisible && isRendered) {
      // Add a slight delay before unmounting to allow for exit animations
      const unmountTimer = setTimeout(() => {
        setIsRendered(false);
      }, 300);
      
      return () => clearTimeout(unmountTimer);
    }
  }, [isVisible]);
  
  if (!isVisible && !isRendered) return null;
  
  const safeStreak = Math.max(1, Math.min(streak || 1, 30));
  const rankTitle = getStreakRank(safeStreak) || "Baby Babbler";
  const description = getRankDescription(safeStreak);
  
  // Handle the card click/tap - only dismiss if animation is complete
  const handleCardClick = () => {
    if (animationComplete) {
      onClose();
    }
  };
  
  return (
    <div 
      ref={containerRef}
      className={`${isLivestream ? 'livestream-rank-shell' : 'fixed inset-0'} flex items-center justify-center z-[10000] overflow-hidden`}
      style={{ 
        fontFamily: "'Inter', system-ui, sans-serif",
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        transition: 'opacity 300ms ease, visibility 300ms ease'
      }}
    >
      <div 
        className={`${isLivestream ? 'absolute' : 'fixed'} inset-0 bg-black/80`}
        onClick={animationComplete ? onClose : undefined}
      />
      
      <div 
        className={`relative z-[10001] flex flex-col items-center pt-6 ${isLivestream ? 'livestream-rank-card-wrap' : ''}`}
        style={{ 
          transform: animationComplete 
            ? 'rotate(0deg) scale(1)' 
            : 'scale(0) rotate(360deg)',
          transition: 'all 700ms cubic-bezier(0.34, 1.56, 0.64, 1)',
          width: "min(80vw, 310px)",
          maxWidth: "310px",
          height: "auto",
        }}
      >
        {animationComplete && (
          <div className="mb-4 text-center">
            <div className="font-bold flex flex-col items-center">
              <span style={{ color: "#ffd700", fontSize: "1.5rem" }}>
                YOU WON!
              </span>
              <span style={{ color: "#ffd700", fontSize: "1.1rem" }}>
                New Streak Unlocked!
              </span>
            </div>
          </div>
        )}
        
        <div 
          className="relative w-full"
          onClick={handleCardClick}
          style={{ cursor: animationComplete ? 'pointer' : 'default' }}
        >
          <RankCard
            index={safeStreak}
            title={rankTitle}
            description={description}
            isCurrentRank={true}
            className="animated-rank-card w-full h-full rounded-lg"
            showSparkles={animationComplete}
          />
          
          {animationComplete && (
            <Button 
              onClick={(e) => {
                e.stopPropagation(); // Prevent the card click from triggering
                onClose();
              }}
              className="absolute -top-2 -right-2 z-50 h-8 w-8 p-0 rounded-full bg-gray-800 border border-gray-600 flex items-center justify-center shadow-xl"
              size="icon"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
